/* iter-113 — TilePanelDialog.
 * Slim wrapper that delegates the recursive folder/link/file rendering
 * to ./TileFolderTree (split out to keep babel-loader's AST traversal
 * within Node's default stack limit).
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Folder, FolderPlus, ChevronRight, X, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { LinkRow, FileRow, AddFolderForm, AddLinkForm, FolderTree } from './TileFolderTree';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EDITOR_ROLES = new Set(['admin', 'engineer', 'editor']);

function currentRole() {
  try { return (typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : ''); } catch { return ''; }
}

function authHeaders() {
  const t = (typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null) || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function TilePanelDialog({ open, onClose, tile, onPrimaryAction }) {
  const role = currentRole();
  const canEdit = EDITOR_ROLES.has(role);
  const tileKey = tile?.id || tile?.key || tile?.label;

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [links, setLinks] = useState([]);
  const [addingRootFolder, setAddingRootFolder] = useState(false);
  const [addingRootLink, setAddingRootLink] = useState(false);

  useEffect(() => {
    if (!tile) return;
    setFolders(tile.tile_folders || []);
    setFiles(tile.tile_files || []);
    setLinks(tile.tile_links || []);
  }, [tile]);

  const reload = useCallback(async () => {
    if (!tileKey) return;
    try {
      const { data } = await axios.get(
        `${API}/admin/dashboard-buttons/tile/${encodeURIComponent(tileKey)}/contents`,
        { headers: authHeaders() }
      );
      setFolders(data.folders || []);
      setFiles(data.files || []);
      setLinks(data.links || []);
    } catch { /* swallow */ }
  }, [tileKey]);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const childrenByParent = useMemo(() => {
    const m = {};
    folders.forEach((f) => {
      const p = f.parent_id || null;
      if (!m[p]) m[p] = [];
      m[p].push(f);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return m;
  }, [folders]);

  const filesByFolder = useMemo(() => {
    const m = {};
    files.forEach((f) => {
      const k = f.folder_id || null;
      if (!m[k]) m[k] = [];
      m[k].push(f);
    });
    return m;
  }, [files]);

  const linksByFolder = useMemo(() => {
    const m = {};
    links.forEach((ln) => {
      const k = ln.folder_id || null;
      if (!m[k]) m[k] = [];
      m[k].push(ln);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return m;
  }, [links]);

  const createFolder = async ({ name, parent_id }) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/dashboard-buttons/tile/${encodeURIComponent(tileKey)}/folder`,
        { name, parent_id }, { headers: authHeaders() }
      );
      setFolders((arr) => [...arr, data]);
      toast.success(`Folder "${data.name}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create folder');
    }
  };

  const deleteFolder = async (folder, cascade) => {
    try {
      await axios.delete(
        `${API}/admin/dashboard-buttons/tile/${encodeURIComponent(tileKey)}/folder/${folder.id}${cascade ? '?cascade=true' : ''}`,
        { headers: authHeaders() }
      );
      toast.success(`Folder "${folder.name}" deleted`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const createLink = async ({ label, url, folder_id }) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/dashboard-buttons/tile/${encodeURIComponent(tileKey)}/link`,
        { label, url, folder_id }, { headers: authHeaders() }
      );
      setLinks((arr) => [...arr, data]);
      toast.success('Link added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add link');
    }
  };

  const deleteLink = async (ln) => {
    if (!window.confirm(`Delete link "${ln.label}"?`)) return;
    try {
      await axios.delete(
        `${API}/admin/dashboard-buttons/tile/${encodeURIComponent(tileKey)}/link/${ln.id}`,
        { headers: authHeaders() }
      );
      setLinks((arr) => arr.filter((x) => x.id !== ln.id));
      toast.success('Link deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete file "${f.filename}"?`)) return;
    try {
      await axios.delete(
        `${API}/admin/dashboard-buttons/tile/${encodeURIComponent(tileKey)}/file/${f.id}`,
        { headers: authHeaders() }
      );
      setFiles((arr) => arr.filter((x) => x.id !== f.id));
      toast.success('File deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (!tile) return null;
  const color = tile.color || '#1F4FBE';
  const actions = { createFolder, createLink, deleteFolder, deleteLink, deleteFile };
  const rootKids = childrenByParent[null] || [];
  const rootFiles = filesByFolder[null] || [];
  const rootLinks = linksByFolder[null] || [];
  const isEmpty = !folders.length && !files.length && !links.length;

  const hasPrimary = tile.action_type === 'workspace'
    ? !!tile.action_target
    : (tile.action_type === 'url' && !!tile.action_target);
  const primaryLabel = tile.action_type === 'workspace' ? 'Open workspace folder' : 'Open external link';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="tile-panel-dialog"
        className="w-[95vw] max-w-xl p-0"
        style={{ backgroundColor: '#F4F1EB' }}
      >
        <div
          className="px-5 sm:px-6 pt-5 pb-4 border-b"
          style={{ borderColor: '#EAE5D9', background: `linear-gradient(135deg,${color},${color}cc)` }}
        >
          <DialogHeader>
            <DialogTitle
              className="text-2xl text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="tile-panel-title"
            >
              {tile.label || 'Department'}
            </DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-wider font-bold mt-1" style={{ color: 'rgba(255,255,255,.85)', fontFamily: 'Manrope' }}>
              {files.length} file{files.length === 1 ? '' : 's'} · {links.length} link{links.length === 1 ? '' : 's'} · {folders.length} folder{folders.length === 1 ? '' : 's'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-4 sm:p-5 max-h-[65vh] overflow-y-auto">
          {canEdit && (
            <div className="flex items-center gap-2 mb-3 pb-3 border-b" style={{ borderColor: '#EAE5D9' }}>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                Admin · Quick add
              </span>
              <button
                type="button"
                onClick={() => setAddingRootFolder((v) => !v)}
                className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded"
                style={{ background: color, color: 'white', fontFamily: 'Manrope' }}
                data-testid="tile-panel-add-root-folder-btn"
              >
                <FolderPlus className="w-3.5 h-3.5" /> Folder
              </button>
              <button
                type="button"
                onClick={() => setAddingRootLink((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded"
                style={{ background: '#1A2E2E', color: 'white', fontFamily: 'Manrope' }}
                data-testid="tile-panel-add-root-link-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Link
              </button>
            </div>
          )}

          {canEdit && addingRootFolder && (
            <div className="mb-2">
              <AddFolderForm color={color} parentId={null} onCreate={createFolder} onCancel={() => setAddingRootFolder(false)} />
            </div>
          )}
          {canEdit && addingRootLink && (
            <div className="mb-2">
              <AddLinkForm color={color} folderId={null} onCreate={createLink} onCancel={() => setAddingRootLink(false)} />
            </div>
          )}

          {(rootLinks.length > 0 || rootFiles.length > 0) && (
            <div className="space-y-1.5 mb-3">
              {folders.length > 0 && (
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  Unfiled
                </p>
              )}
              {rootLinks.map((ln) => (
                <LinkRow key={ln.id} ln={ln} color={color} canEdit={canEdit} onDelete={deleteLink} />
              ))}
              {rootFiles.map((f) => (
                <FileRow key={f.id} f={f} color={color} canEdit={canEdit} onDelete={deleteFile} />
              ))}
            </div>
          )}

          {rootKids.length > 0 && (
            <FolderTree
              color={color}
              canEdit={canEdit}
              rootKids={rootKids}
              childrenByParent={childrenByParent}
              filesByFolder={filesByFolder}
              linksByFolder={linksByFolder}
              actions={actions}
            />
          )}

          {isEmpty && (
            <p className="text-center text-sm italic py-6" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              No files, folders, or links yet.{canEdit ? ' Use Quick add above to attach.' : ''}
            </p>
          )}
        </div>

        {hasPrimary && (
          <div className="px-5 sm:px-6 py-3 border-t flex items-center justify-between gap-2" style={{ borderColor: '#EAE5D9', background: 'white' }}>
            <button
              type="button"
              onClick={onClose}
              data-testid="tile-panel-close"
              className="inline-flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg"
              style={{ background: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
            >
              <X className="w-3.5 h-3.5" /> Close
            </button>
            <button
              type="button"
              onClick={() => onPrimaryAction?.(tile)}
              data-testid="tile-panel-primary"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg text-white"
              style={{ background: color, fontFamily: 'Manrope' }}
            >
              <Folder className="w-3.5 h-3.5" />
              {primaryLabel}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
